import { checkStoreAvailability, checkOfferAvailability } from "@Src/api/storeApi";
import { isDateWithinRange } from "@Src/utils/date.utils";
import { find } from "lodash";

/**
 * jsonCampaignFilenameMapping
 * @desc: Mapping of campaign channel placements to their respective JSONp file names.
 */
export const jsonCampaignTypes = {
  POPUP: "popup",
  TOPBAR: "topbar",
  SEASONAL: "seasonal_tiles",
};

/**
 * jsonCampaignFilenameMapping
 * @desc: Mapping of campaign channel placements to their respective JSONp file names.
 */
export const jsonCampaignFilenameMapping = {
  popup: "popup_modal",
  topbar: "top_bar",
  seasonal_tiles: "seasonal_tiles",
};

/**
 * getCampaignAssets
 * @desc: Helper function to return specified assets from a campaign spot, based on asset type.
 * @param {*} campaignSpot The spot to grab the asset from.
 * @param {*} assetType The asset group to look within. imageAssets, textAssets, linkAssets.
 * @param {*} type The 'type' attribute of the asset to grab. eg. 'POPUP_HEADING'
 * @returns found asset or null
 */
export const getCampaignAssets = (campaignSpot, types = []) => {
  if (!campaignSpot) {
    return {};
  }
  const { imageAssets, textAssets, linkAssets, colorAssets } = campaignSpot;
  const assetsList = [
    ...(imageAssets || []),
    ...(textAssets || []),
    ...(linkAssets || []),
    ...(colorAssets || []),
  ];
  const assets = {};
  types.forEach((type) => {
    assets[type] = find(assetsList, ["type", type]) || null;
  });
  return assets;
};

/**
 * campaignDataFilterUtils
 * @desc: Utility functions for filtering/validating that campaign data should be displayed.
 * @functions
 *    hasVisibleStatus: should the spot/campaign be visible based on status
 *    datesInRange: is the today within the campaign spot run dates range
 *    hasAvailablePark: is the campaign spot related to a park and/or attraction AND is that attraction available to the program
 */
export const campaignDataFilterUtils = {
  hasVisibleStatus: (status, isPreviewProgram) => {
    if (status === "INACTIVE") {
      return false;
    }
    if (status === "DRAFT" && !isPreviewProgram) {
      return false;
    }
    return true;
  },
  datesInRange: (dates) => {
    const { start, end } = dates;
    if (start || end) {
      if (!isDateWithinRange(start, end)) {
        return false;
      }
    }
    return true;
  },
  matchesTravelState: (travelState, travelEnabled) => {
    if (travelState === travelEnabled) {
      return true;
    } else if (travelState !== travelEnabled) {
      return false;
    }
    return true;
  },
  hasAvailableTravelTab: (tab, travelEnabled, travelTabsConfig) => {
    if (!travelEnabled) {
      return false;
    }
    if (tab) {
      switch (tab) {
        case "parks":
          return travelTabsConfig.theme_park_tab_enabled;
        case "cars":
          return travelTabsConfig.cars_tab_enabled;
        case "hotels":
          return travelTabsConfig.hotels_tab_enabled;
        case "activities":
          return travelTabsConfig.activities_tab_enabled;
        case "flights":
          return travelTabsConfig.flights_tab_enabled;
        case "events":
          return travelTabsConfig.events_tab_enabled;
        case "cruises":
          return travelTabsConfig.cruise_tab_enabled;
        default:
          return false;
      }
    }
    return true;
  },
  hasAvailablePark: (attraction, attractions, isParksTabEnabled) => {
    if (attraction) {
      // check if the parks tab is enabled for the program
      if (isParksTabEnabled === false) {
        return false;
      } else if (attraction) {
        // check if the campaign spot is related to an attraction AND that attraction is available to the program
        const hasAttraction = attractions.find((attr) => attr.attractionIdentifier === attraction);
        return Boolean(hasAttraction);
      }
    }
    // even if subscriptionRequirements.attraction or travelTab is not set, the campaign spot is still valid, so return true
    return true;
  },
  hasAvailableRetailer: (retailer, attractions) => {
    if (retailer) {
      // check if the campaign spot is related to a retailer AND that retailer is available to the program
      const hasRetailer = attractions.find((attr) => attr.attractionIdentifier === retailer);
      return Boolean(hasRetailer);
    }
    return true;
  },
  hasAvailableBrand: (brandKey) => {
    return new Promise((resolve) => {
      if (brandKey) {
        // check if the campaign spot is related to a brand AND that brand is available to the program
        checkStoreAvailability(brandKey).then((response) => {
          const brandResult = response.find((item) => {
            return item.key && item.key.toString() === brandKey.toString();
          });
          if (brandResult && brandResult.hasOwnProperty("available")) {
            resolve(brandResult.available);
          } else {
            resolve(false);
          }
        });
      } else {
        resolve(true);
      }
    });
  },
  hasAvailableOffer: (offerKey) => {
    return new Promise((resolve) => {
      if (offerKey) {
        // check if the campaign spot is related to an offer AND that offer is available to the program
        checkOfferAvailability(offerKey).then((response) => {
          const offerResult = response.find((item) => {
            return item.key && item.key.toString() === offerKey.toString();
          });
          if (offerResult && offerResult.hasOwnProperty("available")) {
            resolve(offerResult.available);
          } else {
            resolve(false);
          }
        });
      } else {
        resolve(true);
      }
    });
  },
  targetSpecificSubscription: (specificSubscription, programSubscriptions) => {
    if (!specificSubscription || !programSubscriptions) {
      return false;
    }

    const programValues = programSubscriptions
      .split(",")
      .map((value) => value.trim().toUpperCase());

    const hasIndividual = programValues.includes(specificSubscription.toUpperCase());
    const hasInvalidSubscription = ["BASIC", "TRANSACTIONAL"].some((invalid) =>
      programValues.includes(invalid),
    );

    return hasIndividual && hasInvalidSubscription;
  },
};
